import type { UIEventHandler } from 'react'
import { useCallback, useEffect, useRef } from 'react'

import { useVirtualizer } from '@tanstack/react-virtual'

interface Params {
  canLoadMore: boolean
  onLoadMore: () => void
  itemHeight: number
  itemsLength: number
}

export const useVerticalInfiniteScroll = ({
  canLoadMore,
  onLoadMore,
  itemHeight,
  itemsLength,
}: Params) => {
  const parentRef = useRef<HTMLDivElement | null>(null)

  const fetchMoreOnBottomReached = useCallback(
    (containerRefElement?: HTMLDivElement | null) => {
      if (containerRefElement) {
        const { scrollHeight, scrollTop, clientHeight } = containerRefElement

        if (
          scrollHeight - scrollTop - clientHeight < itemHeight &&
          canLoadMore
        ) {
          onLoadMore()
        }
      }
    },
    [onLoadMore, canLoadMore, itemHeight],
  )

  useEffect(() => {
    fetchMoreOnBottomReached(parentRef.current)
  }, [fetchMoreOnBottomReached])

  const virtualizer = useVirtualizer({
    count: itemsLength,
    getScrollElement: () => parentRef.current,
    estimateSize: () => itemHeight,
    overscan: 5,
  })

  const handleScroll: UIEventHandler<HTMLDivElement> = (event) => {
    fetchMoreOnBottomReached(event.target as HTMLDivElement)
  }

  return {
    parentRef,
    virtualizer,
    handleScroll,
  }
}
